// client/src/components/business/BusinessList.js - REPLACE ENTIRE FILE
import React, { useState, useEffect, useMemo, memo, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import businessService from "../../services/businessService";
import BusinessCard from "./BusinessCard";
import LoadingSpinner from "../common/LoadingSpinner";
import { useUserAuth } from "../../contexts/UserAuthContext";
import { getActiveFilterCount } from "../../utils/filterUtils";
// CSS loaded at page level - removed duplicate import

const PAGE_SIZE = 12; // keep skeletons in sync with initial fetch size

const BusinessList = memo(({ searchTerm, filters, onFilterChange }) => {
    const { t } = useTranslation();
    // Get user context for BusinessCard re-rendering when auth state changes
    const { user } = useUserAuth();

    // Sorting state
    const [sortBy, setSortBy] = useState('');
    const [sortOrder, setSortOrder] = useState('desc');

    // Transform filters for API call
    const apiFilters = {
        search: searchTerm,
        categories: filters.categories,
        cities: filters.cities,
        businessTypes: filters.businessTypes,
        verified: filters.verified,
        sortBy: sortBy,
        order: sortOrder,
    };

    // Infinite query for businesses
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        isError,
        error,
        refetch,
    } = useInfiniteQuery({
        queryKey: ["businesses", searchTerm, JSON.stringify(filters), sortBy, sortOrder],
        queryFn: ({ pageParam = 1 }) =>
            businessService.getBusinesses({
                page: pageParam,
                limit: PAGE_SIZE,
                ...apiFilters,
            }),
        getNextPageParam: (lastPage) => {
            return lastPage.pagination.hasNext
                ? lastPage.pagination.currentPage + 1
                : undefined;
        },
        // Keep previous data while refetching
        keepPreviousData: true,
        // Force revalidation so home feed reflects changes immediately
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        // Use global cache settings for consistency
    });

    // Direct calculation - no unnecessary state
    const businesses = useMemo(() => {
        return data ? data.pages.flatMap((page) => page.businesses) : [];
    }, [data]);

    // IntersectionObserver sentinel for infinite scroll (replaces scroll polling)
    const sentinelRef = useRef(null);
    useEffect(() => {
        if (!hasNextPage) return; // nothing more to load
        const el = sentinelRef.current;
        if (!el) return;
        if (!('IntersectionObserver' in window)) {
            // Fallback: if IO is unavailable, do a one-time next page fetch near load
            if (!isFetchingNextPage) fetchNextPage();
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            { root: null, rootMargin: "600px 0px", threshold: 0 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    // Count active filters (shared util)
    const activeFilterCount = getActiveFilterCount(filters);

    // Get filter summary for display
    const getFilterSummary = () => {
        const parts = [];
        if (searchTerm) parts.push(`"${searchTerm}"`);
        if (filters.categories?.length) {
            parts.push(
                `${filters.categories.length} ${t(`businessList.category${filters.categories.length === 1 ? '' : '_plural'}`)}`
            );
        }
        if (filters.cities?.length) {
            parts.push(
                `${filters.cities.length} ${t(`businessList.city${filters.cities.length === 1 ? '' : '_plural'}`)}`
            );
        }
        if (filters.businessTypes?.length) {
            parts.push(
                `${filters.businessTypes.length} ${t(`businessList.businessType${filters.businessTypes.length === 1 ? '' : '_plural'}`)}`
            );
        }
        if (filters.verified) {
            parts.push(
                filters.verified === "true"
                    ? t('businessList.verifiedOnly')
                    : t('businessList.unverifiedOnly')
            );
        }
        return parts.join(", ");
    };

    const filterSummary = getFilterSummary();
    const totalResults = data?.pages?.[0]?.pagination?.totalBusinesses || 0;

    // Render skeleton list to reserve layout and reduce CLS on first load
    if (isLoading) {
        const skeletons = Array.from({ length: PAGE_SIZE });
        return (
            <div className="business-list">
                <div className="results-header">
                    <div className="results-info">
                        <h2>&nbsp;</h2>
                    </div>
                </div>
                <div id="business-grid" role="region" aria-label="Loading business listings" tabIndex="-1">
                    <div className="business-grid" role="list" aria-busy="true">
                        {skeletons.map((_, idx) => (
                            <div className="skeleton-card" role="listitem" aria-hidden="true" key={`skeleton-${idx}`}>
                                <div className="skeleton-avatar" />
                                <div className="skeleton-content">
                                    <div className="skeleton-line short" />
                                    <div className="skeleton-line" />
                                    <div className="skeleton-line" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="error-message">
                <p>{t('businessList.errorLoading')}: {error.message}</p>
                <button onClick={() => refetch()}>{t('businessList.tryAgain')}</button>
            </div>
        );
    }

    return (
        <div className="business-list">
            {/* Skip link for screen readers */}
            <a href="#business-grid" className="sr-only-focusable skip-to-content">
                {t('businessList.skipToListings')}
            </a>

            {/* Announce results to screen readers */}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
            >
                {isLoading
                    ? t('businessList.loading')
                    : t(`businessList.resultsCount${totalResults === 1 ? '' : '_plural'}`, { count: totalResults })
                }
            </div>

            {/* Results Header */}
            <div className="results-header">
                <div className="results-info">
                    <h2>
                        {totalResults === 0
                            ? t('businessList.noResults')
                            : t(`businessList.resultsCount${totalResults === 1 ? '' : '_plural'}`, { count: totalResults })
                        }
                    </h2>
                    {filterSummary && (
                        <p className="filter-summary">
                            {t('businessList.filteredBy')}: {filterSummary}
                        </p>
                    )}
                </div>

                <div className="results-actions">
                    {/* Sort Dropdown */}
                    <div className="sort-dropdown">
                        <label htmlFor="sort-select">{t('businessList.sortBy')}:</label>
                        <select
                            id="sort-select"
                            value={sortBy}
                            onChange={(e) => {
                                setSortBy(e.target.value);
                                // Reset to default order when changing sort type
                                if (e.target.value === '') {
                                    setSortOrder('desc');
                                }
                            }}
                        >
                            <option value="">{t('businessList.defaultSort')}</option>
                            <option value="facebookLikes">{t('businessList.facebookLikes')}</option>
                            <option value="createdAt">{t('businessList.dateAdded')}</option>
                            <option value="averageRating">{t('businessList.ratings')}</option>
                        </select>

                        {/* Order toggle - only show when a sort is selected */}
                        {sortBy && (
    <button
        className="sort-order-toggle"
        onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
        title={sortOrder === 'desc' ? t('businessList.descending') : t('businessList.ascending')}
        aria-label={sortOrder === 'desc' ? t('businessList.descending') : t('businessList.ascending')}
    >
        {sortOrder === 'desc' ? (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 16l-6-6h12z" fill="currentColor"/></svg>
        ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M12 8l6 6H6z" fill="currentColor"/></svg>
        )}
    </button>
)}
                    </div>

                    {activeFilterCount > 0 && (
                        <div className="active-filters-count">
                            <span className="filter-badge">
                                {t(`businessList.activeFilter${activeFilterCount === 1 ? '' : '_plural'}`, { count: activeFilterCount })}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Business Grid */}
            {businesses.length === 0 ? (
                <div className="no-results">
                    <div className="no-results-icon" aria-hidden="true"></div>
                    <h3>{t('businessList.noResults')}</h3>
                    <p>
                        {searchTerm || activeFilterCount > 0
                            ? t('businessList.noResultsDesc')
                            : t('businessList.noResultsDescEmpty')}
                    </p>
                    {activeFilterCount > 0 && (
                        <button
                            className="clear-filters-suggestion"
                            onClick={() => onFilterChange({})}
                        >
                            {t('businessList.clearAllFilters')}
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {/* Results region (avoid nested <main>) */}
                    <div id="business-grid" role="region" aria-label={t('businessList.resultsRegion', { defaultValue: 'Business listings' })} tabIndex="-1">
                        <div className="business-grid" role="list">
                            {businesses.map((business) => (
                                <div key={`${business._id}-${user?._id || 'guest'}`} role="listitem">
                                    <BusinessCard business={business} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Loading indicator for infinite scroll */}
                    {isFetchingNextPage && (
                        <div className="loading-more">
                            <LoadingSpinner />
                            <p>{t('businessList.loadingMore')}</p>
                            <div
                                role="status"
                                aria-live="polite"
                                className="sr-only"
                            >
                                {t('businessList.loadingMore')}
                            </div>
                        </div>
                    )}

                    {/* Sentinel for IntersectionObserver infinite loading */}
                    <div ref={sentinelRef} aria-hidden="true" />

                    {/* End of results message */}
                    {!hasNextPage && businesses.length > 0 && (
                        <div className="end-message">
                            <p>
                                {t(`businessList.youveSeen${totalResults === 1 ? '' : '_plural'}`, { count: totalResults })}
                                {filterSummary &&
                                    " " + t('businessList.adjustFilters')}
                            </p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

// Add display name for better debugging
BusinessList.displayName = 'BusinessList';

export default BusinessList;

